/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Button, Form, Grid, Header } from 'semantic-ui-react'

import { useTranslation } from 'react-i18next'
import { removeCategorySlot } from '../../services/Category/Category.service'
import { useMutation } from '@tanstack/react-query'
import { ultyToast } from '../Shared/UltyToast/UltyToast'

const RemoveSlotConfirmation = ({set, slot, afterSubmit}) => {
    const [t] = useTranslation()

    const [loading, setLoading] = useState(false)

    const handleSubmit = async () => {
        setLoading(true)

        try {
            const result = await removeCategorySlot(set, slot.id, false)
            if (result.code === 'OVERRIDES_EXIST') {
                afterSubmit(result)
            } else {
                afterSubmit()
                ultyToast({ type: 'success', title: t('global.registerSuccess') })
            }
        } catch (e) {
            ultyToast({ type: 'error', title: t('global.anErrorOccurred') })
        } finally {
            setLoading(false)
        }
    }

    return (
      <Form>
          <Grid css={css`
              .column {
                  padding-top: .5rem !important;
                  padding-bottom: .5rem !important;
              }
          `}>
              <Grid.Column width={16}>
                  <Header>{t('category_slot.delete_specific_slot', {name: slot.name})}</Header>
              </Grid.Column>
              <Grid.Column width={16}>
                  <div style={{textAlign: 'right', marginTop: '10px'}}>
                      <Button type="submit" color="red" loading={loading} disabled={loading}
                              onClick={handleSubmit}>{t('global.delete')}</Button>
                  </div>
              </Grid.Column>
          </Grid>
      </Form>
    )
}

const RemoveSlotWithOverridesConfirmation = ({set, slot, afterSubmit, pointOfSales}) => {
    const [t] = useTranslation()

    const deleteSlotMutation = useMutation({
        mutationFn: async () => {
            await removeCategorySlot(set, slot.id, true)
        },
        onSuccess: () => {
            ultyToast({ type: 'success', title: t('global.registerSuccess') })
            afterSubmit()
        },
        onError: () => {
            ultyToast({ type: 'error', title: t('global.anErrorOccurred') })
        },
    })

    return (
      <Form>
          <Grid css={css`
              .column {
                  padding-top: .5rem !important;
                  padding-bottom: .5rem !important;
              }
          `}>
              <Grid.Column width={16}>
                  <Header>{t('category_slot.following_pos_impacted')}</Header>
                  <>
                      {pointOfSales.map(pointOfSale => (
                        <p key={pointOfSale.id}>{pointOfSale.name}</p>
                      ))}
                  </>
              </Grid.Column>
              <Grid.Column width={16}>
                  <div style={{textAlign: 'right', marginTop: '10px'}}>
                      <Button
                        type="button"
                        loading={deleteSlotMutation.isPending}
                        disabled={deleteSlotMutation.isPending}
                        onClick={afterSubmit}
                      >
                          {t('global.cancel')}
                      </Button>
                      <Button
                        type="submit"
                        color="red"
                        loading={deleteSlotMutation.isPending}
                        disabled={deleteSlotMutation.isPending}
                        onClick={deleteSlotMutation.mutate}>
                          {t('global.delete')}
                      </Button>
                  </div>
              </Grid.Column>
          </Grid>
      </Form>
    )
}

RemoveSlotConfirmation.propTypes = {
    set: PropTypes.string.isRequired,
    slot: PropTypes.object.isRequired,
    afterSubmit: PropTypes.func.isRequired,
}

export { RemoveSlotConfirmation, RemoveSlotWithOverridesConfirmation }
